import React, { useEffect, useMemo, useState } from 'react';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  SelectChangeEvent,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  MobileStateQuestion,
  SaveDataAnswerGroup,
  SaveDataAnswerType,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { Platform } from '../../constants/platform';
import { PrepPopModal } from '../../components/Questions/PrepPopModal';
import {
  confirmData,
  QuestionConfirmData,
} from '../../components/Questions/QuestionConfirmData';

interface CustomMultiSelectProps {
  label?: string;
  options?: { value: string | number; label: string; disabled?: boolean }[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  error?: boolean;
  helperText?: string;
  // other props can be included here as needed
  [key: string]: any;
}

const StyledFormControl = styled(FormControl)(({ theme }) => {
  const palette = theme.palette || { primary: { main: '#1976d2' } }; // Fallback value for theme
  return {
    marginTop: '16px',
    position: 'relative',
    '& .MuiInputLabel-root': {
      backgroundColor: '#fff',
      padding: '0 4px',
      left: '-4px',
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled':
      {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: '#fff',
        // color: palette.primary.main,
      },
    '& .MuiOutlinedInput-notchedOutline': {
      // borderColor: palette.primary.main,
    },
    '& .MuiSelect-root': {
      padding: '16.5px 14px',
    },
  };
});

type MultiSelectProps = CustomMultiSelectProps & {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
  disabled?: boolean;
  showTitle?: boolean;
};

const CustomMultiSelect: React.FC<MultiSelectProps> = ({
  question,
  answers,
  stringMapper,
  onSetAnswer,
  disabled,
  showTitle,
}) => {
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Track dropdown state
  const [isAnswered, setIsAnswered] = useState<boolean>(question.Completed);
  const [changed, setChanged] = useState<boolean>(false);

  const label = useMemo(() => {
    if (!showTitle) {
      return '';
    }
    if (stringMapper) {
      return stringMapper(question?.Title);
    }
    return question?.Title;
  }, [showTitle, question?.Title, stringMapper]);

  const [confirmAnswers, setConfirmAnswer] = useState<string[] | undefined>();
  const setAnswer = (newanswers: string[]) => {
    if (answers?.AnswerType === SaveDataAnswerType.PrePop) {
      setConfirmAnswer(newanswers);
    } else {
      setUserAnswers(newanswers);
      if (onSetAnswer) onSetAnswer(question.QuestionKey, newanswers);
    }
  };

  useEffect(() => {
    const newAnswers: string[] = [];
    if (answers !== undefined) {
      for (const item of answers.Answers) {
        if (item.Active) {
          newAnswers.push(item.Answer);
        }
      }
      setIsAnswered(newAnswers.length > 0 ? true : false);
    }
    setUserAnswers(newAnswers);
  }, [answers]);

  const handleChange = (event: SelectChangeEvent<(string | number)[]>) => {
    if (disabled) return;
    setChanged(true);
    const selections = event.target.value as string[];
    const result = getSelectedValue(selections);
    setAnswer(result);
  };

  const getSelectedValue = (selections: string[]) => {
    const answers = [];
    for (const item of selections) {
      const option = question.Answers.find((option) => option.TextKey === item);
      answers.push(option);
    }
    const anyTerminal = answers.find((item) => item?.Terminal === 'Y');

    if (anyTerminal) {
      if (userAnswers.includes(anyTerminal.TextKey)) {
        const nonTerminal = answers.filter((item) => item?.Terminal !== 'Y');
        const values = nonTerminal.map((ans) => ans?.TextKey) as string[];
        return values;
      } else {
        return [anyTerminal.TextKey];
      }
    } else {
      const values = answers.map((ans) => ans?.TextKey) as string[];
      return values;
    }
  };

  const onClose = () => {
    setIsDropdownOpen(false); // Close dropdown
    if (onSetAnswer && changed) {
      setTimeout(() => {
        onSetAnswer(question.QuestionKey, userAnswers);
      }, 50);
    }
  };

  const onOpen = () => {
    setIsDropdownOpen(true); // Open dropdown
  };

  if (question.ReadOnly) {
    return (
      <React.Fragment>
        {userAnswers.map((item, index) => {
          const option = question.Answers.find((opt) => opt.TextKey === item);
          const text =
            option?.OverrideText ?? (stringMapper ? stringMapper(item) : item);
          return (
            <React.Fragment key={item}>
              {text}
              <br />
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Box>
      {/* Overlay Box */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          //  backgroundColor: isDropdownOpen ? 'rgba(0, 0, 0, 0.3)' : 'transparent', // Grey overlay when dropdown is open
          zIndex: isDropdownOpen ? 1 : -1, // Ensure it appears behind the dropdown
          transition: 'background-color 0.3s ease', // Smooth transition for overlay
        }}
        onClick={onClose} // Close dropdown when overlay is clicked
      />
      <StyledFormControl
        fullWidth
        variant='outlined'
        error={false}
        disabled={disabled}
        sx={{
          position: 'relative',
          zIndex: 0, // Ensure dropdown is above overlay
        }}
      >
        {Platform?.isAudit() ? label : null}
        <Select
          multiple
          open={isDropdownOpen}
          onOpen={onOpen} // Handle opening
          sx={{
            '&.Mui-disabled': {
              cursor: 'not-allowed !important',
              // color: 'rgba(0, 0, 0, 0.38)', // Optional: Customize disabled color,
              pointerEvents: 'all !important', // Override pointer-events: none
            },
          }}
          value={userAnswers}
          renderValue={(selected) => {
            const selectedNotEmpty = selected?.filter(
              (item) =>
                item != null &&
                item !== '' &&
                question?.Answers?.some((a) => a.TextKey === item)
            );
            if (!selectedNotEmpty || selectedNotEmpty.length === 0) {
              return 'No selection';
            }

            return disabled
              ? selectedNotEmpty.map((item, index) => {
                  const option = question.Answers.find(
                    (opt) => opt.TextKey === item
                  );
                  const text =
                    option?.OverrideText ??
                    (stringMapper ? stringMapper(item) : item);
                  return (
                    <React.Fragment key={item}>
                      {text}
                      {index < selectedNotEmpty.length - 1 && <br />}
                    </React.Fragment>
                  );
                })
              : `${selectedNotEmpty.length} Selected`;
          }}
          label={''}
          onChange={handleChange}
          onClose={onClose}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '8px',
              backgroundColor: 'white',
              borderBottom: '1px solid #e0e0e0',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Button
              onClick={onClose}
              variant='text'
              size='small'
              sx={{
                minWidth: 'auto', // Prevent unnecessary width
                padding: '4px 8px',
                fontSize: '0.875rem',
                textTransform: 'none', // Maintain natural casing
                lineHeight: 'normal',
              }}
            >
              CLOSE
            </Button>
          </Box>
          {question?.Answers?.map((option) => (
            <MenuItem key={option?.TextKey} value={option?.TextKey}>
              <Checkbox checked={userAnswers?.includes(option?.TextKey)} />
              <ListItemText
                primary={option?.OverrideText ?? stringMapper(option?.TextKey)}
              />
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>

      {confirmAnswers && (
        <PrepPopModal
          visible={confirmAnswers !== undefined}
          hide={() => {
            setConfirmAnswer(undefined);
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            {confirmData?.title} to{' '}
            {confirmAnswers?.length
              ? confirmAnswers.map((ans, idx) => (
                  <React.Fragment key={idx}>
                    {ans?.toLanguagePack?.() ?? ''}
                    {idx < confirmAnswers.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))
              : ''}
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {confirmData?.message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => {
                setConfirmAnswer(undefined);
              }}
            >
              {confirmData?.cancelText}
            </Button>
            <Button
              variant='contained'
              fullWidth
              onClick={() => {
                if (confirmAnswers !== undefined && onSetAnswer) {
                  setUserAnswers(confirmAnswers);
                  onSetAnswer(question.QuestionKey, confirmAnswers);
                  setConfirmAnswer(undefined);
                }
              }}
            >
              {confirmData?.successText}
            </Button>
          </Box>
        </PrepPopModal>
      )}
    </Box>
  );
};

export default CustomMultiSelect;
